import React, { forwardRef } from 'react';
import './Card.css';

/**
 * Reusable Card component for rendering all types of cards in the game
 * Handles white cards, black cards, submission cards, and special variants
 */
const Card = forwardRef(
  (
    {
      // Card data
      card,
      text, // Alternative to card.text for simple string cards
      id, // Alternative to card.id

      // Card type and styling
      type = 'white', // 'white', 'black', 'submitted', 'winning', 'preview'
      variant = null, // 'write-in', 'result-black', etc.

      // State
      isSelected = false,
      isClickable = false,
      isWriteIn = false,
      selectionOrder = null, // For multi-card selection (1, 2, 3, etc.)
      wagerGroup = null, // RHP Enhanced Wagering (#275): which pick-group (0 = base, 1+ = wager sets)

      // Interaction
      onClick = null,
      onRemove = null, // For removable cards like write-ins

      // Display options
      showDebugId = process.env.NODE_ENV === 'development',
      showSelectionIndicator = true,
      className = '',

      // Content customization
      badge = null, // Custom badge content
      children = null, // Additional content to render inside card
    },
    ref
  ) => {
    // Extract card data from either card object or individual props
    const cardText = text || card?.text || '';
    const cardId = id || card?.id;

    // BUGFIX #244: scale font size down for longer text so it can't overflow/clip
    // out of the fixed-width card. Thresholds tuned for the ~200-220px card width.
    const getContentFontSize = (length) => {
      if (length > 120) return '11px';
      if (length > 90) return '12px';
      if (length > 60) return '14px';
      return null; // short enough — use the CSS default
    };
    const contentFontSize = getContentFontSize(cardText.length);

    // Build CSS classes
    const cardClasses = [
      'card',
      `${type}-card`,
      isSelected && 'selected',
      isClickable && 'clickable',
      isWriteIn && 'write-in-card',
      variant && `card-${variant}`,
      className,
    ]
      .filter(Boolean)
      .join(' ');

    // Handle click events
    const handleClick = () => {
      if (isClickable && onClick) {
        onClick();
      }
    };

    return (
      <div
        ref={ref}
        className={cardClasses}
        onClick={handleClick}
        role={isClickable ? 'button' : undefined}
        tabIndex={isClickable ? 0 : undefined}
        onKeyDown={
          isClickable
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleClick();
                }
              }
            : undefined
        }
      >
        {/* Selection order badge (for multi-card rounds) or selected badge */}
        {isSelected && selectionOrder !== null && (
          <div
            className={
              wagerGroup !== null
                ? `selection-order-badge wager-group-${wagerGroup % 6}`
                : 'selection-order-badge'
            }
          >
            {selectionOrder}
          </div>
        )}
        {isSelected && selectionOrder === null && <div className="selected-badge">✔</div>}

        {/* Write-in badge */}
        {isWriteIn && (
          <div className="write-in-badge">
            <span>✍️</span>
          </div>
        )}

        {/* Custom badge */}
        {badge && <div className="custom-badge">{badge}</div>}

        {/* Main card content */}
        <div
          className="card-content"
          style={contentFontSize ? { fontSize: contentFontSize } : undefined}
        >
          {cardText}
        </div>

        {/* Additional content */}
        {children}

        {/* Selection indicators */}
        {showSelectionIndicator && isSelected && (
          <div className="selection-indicator">
            <div className="radio-button checked"></div>
          </div>
        )}

        {showSelectionIndicator && !isSelected && isClickable && (
          <div className="selection-indicator">
            <div className="radio-button"></div>
          </div>
        )}

        {/* Remove button for removable cards */}
        {onRemove && (
          <button
            className="card-remove-btn"
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
            title="Remove this card"
          >
            ✕
          </button>
        )}

        {/* Debug ID display */}
        {showDebugId && cardId && <div className="card-debug-id">{cardId}</div>}
      </div>
    );
  }
);

Card.displayName = 'Card';

// Convenience components for specific card types
export const WhiteCard = (props) => <Card {...props} type="white" />;
export const BlackCard = (props) => <Card {...props} type="black" />;
export const SubmittedCard = (props) => (
  <Card {...props} type="submitted" showSelectionIndicator={false} />
);
export const WinningCard = (props) => (
  <Card {...props} type="winning" showSelectionIndicator={false} />
);
export const PreviewCard = (props) => (
  <Card {...props} type="preview" showSelectionIndicator={false} />
);
export const WriteInCard = (props) => <Card {...props} isWriteIn={true} />;

export default Card;
